import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, EmptyState, Field, Icon, Input, Select, TextArea, useToast } from "@agentsaas/ui";
import { McpScope, PermissionMode, Template, pickLocalized, type CapabilityRef, type LocalizedText, type PermissionPolicy } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { Loading } from "../layout/Loading";
import { useTemplates } from "../data/queries";
import { TenantSwitcher } from "../pages/ProjectsPage";
import { ShellSlot } from "../layout/shell";
import { NotFound } from "../pages/NotFound";
import { TemplateCapabilities, useMergePreview } from "../capabilities/TemplateCaps";
import { DEFAULT_IMAGE } from "../capabilities/model";

/** 建议的模型（可自由输入；实际允许哪些由 llm-gw / 凭据决定） */
export const MODEL_SUGGESTIONS = ["claude-opus", "claude-sonnet", "claude-haiku"];

/** 模板列表：内置（只读，可复制）+ 本租户自定义（可编辑） */
export function TemplatesPage() {
  const { tid = "" } = useParams();
  const { t, locale } = useT();
  const templates = useTemplates(tid);
  const items = templates.data ?? [];
  return (
    <div className="page">
      <ShellSlot name="title">
        <TenantSwitcher tid={tid} />
      </ShellSlot>
      <div className="page__head">
        <h1 className="page__title">{t("template.title")}</h1>
        <Link className="ui-btn ui-btn--primary ui-btn--md" to={`/t/${tid}/templates/new`} data-testid="template-new">
          <Icon name="plus" />
          {t("template.new")}
        </Link>
      </div>
      {templates.isPending ? (
        <Loading />
      ) : items.length ? (
        <ul className="tpl-list">
          {items.map((x) => (
            <li key={x.id} className="card tpl-item" data-testid="template-item">
              <div className="row">
                <strong className="grow">{pickLocalized(x.name, locale)}</strong>
                <Badge tone={x.tenantId ? "accent" : "neutral"}>{x.tenantId ? t("template.custom") : t("template.builtin")}</Badge>
              </div>
              <div className="muted small">
                <code>{x.role}</code>
                <span className="sep" aria-hidden="true" />
                {t("team.template_version", { version: x.version })}
                <span className="sep" aria-hidden="true" />
                <code>{x.defaultModel}</code>
              </div>
              <p className="small tpl-item__desc">{pickLocalized(x.description, locale)}</p>
              <div className="row">
                <Link className="ui-btn ui-btn--secondary ui-btn--sm" to={`/t/${tid}/templates/${x.id}`} data-testid="template-open">
                  <Icon name={x.tenantId ? "edit" : "file"} />
                  {x.tenantId ? t("template.edit") : t("template.view")}
                </Link>
                <Link className="ui-btn ui-btn--ghost ui-btn--sm" to={`/t/${tid}/templates/new?from=${x.id}`} data-testid="template-duplicate">
                  <Icon name="copy" />
                  {t("template.duplicate")}
                </Link>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="layers" title={t("template.empty")} />
      )}
    </div>
  );
}

interface Draft {
  role: string;
  name: LocalizedText;
  description: LocalizedText;
  systemPrompt: string;
  mcpScopes: McpScope[];
  defaultModel: string;
  permissionDefault: PermissionPolicy["default"];
  capabilities: CapabilityRef[];
}

const sameRefs = (a: CapabilityRef[], b: CapabilityRef[]) => JSON.stringify(a) === JSON.stringify(b);

function draftOf(x: Template | undefined, copy: boolean): Draft {
  if (!x) return { role: "", name: {}, description: {}, systemPrompt: "", mcpScopes: ["identity"], defaultModel: MODEL_SUGGESTIONS[1]!, permissionDefault: "ask", capabilities: [] };
  return {
    role: copy ? `${x.role}-custom`.slice(0, 32) : x.role,
    name: { ...x.name },
    description: { ...x.description },
    systemPrompt: x.systemPrompt,
    mcpScopes: [...x.mcpScopes],
    defaultModel: x.defaultModel,
    permissionDefault: x.permissionPolicy.default,
    capabilities: (x.capabilities ?? []).map((r) => ({ ...r, params: { ...r.params } })),
  };
}

/** 去掉空白的语言项；全空时返回 null */
function cleanText(v: LocalizedText): LocalizedText | null {
  const out = Object.fromEntries(Object.entries(v).filter(([, s]) => s?.trim())) as LocalizedText;
  return Object.keys(out).length ? out : null;
}

/** 模板编辑器：名称与描述中英双语、系统提示、MCP scope、模型、默认权限 */
export function TemplateEditorPage() {
  const { tid = "", tplid } = useParams();
  const [sp] = useSearchParams();
  const templates = useTemplates(tid);
  if (templates.isPending) return <Loading />;
  const from = tplid ?? sp.get("from") ?? undefined;
  const base = templates.data?.find((x) => x.id === from);
  if (from && !base) return <NotFound />;
  return <TemplateEditor key={`${tplid ?? "new"}:${from ?? ""}:${base?.version ?? 0}`} tid={tid} base={base} mode={tplid ? (base?.tenantId ? "edit" : "view") : "create"} />;
}

function TemplateEditor({ tid, base, mode }: { tid: string; base: Template | undefined; mode: "create" | "edit" | "view" }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [d, setD] = useState<Draft>(() => draftOf(base, mode === "create"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const readOnly = mode === "view";
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const setText = (k: "name" | "description", l: "zh-CN" | "en", v: string) => setD((x) => ({ ...x, [k]: { ...x[k], [l]: v } }));

  const roleOk = Template.shape.role.safeParse(d.role).success;
  const name = cleanText(d.name);
  const modelOk = !!d.defaultModel.trim();
  const capBase = { mcpScopes: d.mcpScopes, allowedTools: base?.allowedTools ?? [], image: base?.image ?? DEFAULT_IMAGE };
  const { merged } = useMergePreview(tid, d.capabilities, capBase);
  const capsChanged = !sameRefs(d.capabilities, base?.capabilities ?? []);
  const valid = roleOk && !!name && modelOk && !merged.imageConflict;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || readOnly) return;
    setBusy(true);
    setError(null);
    const common = {
      role: d.role,
      name: name!,
      description: cleanText(d.description) ?? name!,
      systemPrompt: d.systemPrompt,
      mcpScopes: d.mcpScopes,
      defaultModel: d.defaultModel.trim(),
      permissionPolicy: { ...(base?.permissionPolicy ?? { rules: [] }), default: d.permissionDefault },
    };
    try {
      if (mode === "edit" && base) {
        // 基本字段走 templatePatch，能力包走 templateCapabilitiesPut（服务端校验镜像冲突）；各自只在有改动时调用
        const fieldsChanged = JSON.stringify(draftOf(base, false)) !== JSON.stringify({ ...d, capabilities: draftOf(base, false).capabilities });
        let next = base;
        if (fieldsChanged || !capsChanged) next = await api.call("templatePatch", { params: { tplid: base.id }, body: common });
        if (capsChanged) next = await api.call("templateCapabilitiesPut", { params: { tplid: base.id }, body: { capabilities: d.capabilities } });
        toast.show({ message: t("template.saved", { version: next.version }), tone: "success" });
      } else {
        const created = await api.call("templateCreate", {
          params: { tid },
          body: {
            ...common,
            skills: base?.skills ?? [],
            allowedTools: base?.allowedTools ?? [],
            image: base?.image ?? "agentsaas/agent-base:latest",
            resources: base?.resources ?? { cpus: 2, memoryMb: 4096 },
          },
        });
        if (d.capabilities.length) await api.call("templateCapabilitiesPut", { params: { tplid: created.id }, body: { capabilities: d.capabilities } });
        toast.show({ message: t("template.created"), tone: "success" });
      }
      await qc.invalidateQueries({ queryKey: ["templates"] });
      navigate(`/t/${tid}/templates`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "create" ? t("template.new") : pickLocalized(base!.name, locale);
  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">{title}</h1>
        <Link to={`/t/${tid}/templates`}>{t("common.back")}</Link>
      </div>
      {mode === "edit" ? <p className="banner banner--warning small">{t("template.reload_hint", { version: base!.version })}</p> : null}
      {readOnly ? (
        <p className="banner small">
          {t("template.builtin_readonly")}{" "}
          <Link to={`/t/${tid}/templates/new?from=${base!.id}`}>{t("template.duplicate")}</Link>
        </p>
      ) : null}
      <form className="card tpl-form" onSubmit={submit} noValidate>
        <fieldset disabled={readOnly} className="tpl-form__fields">
          <Field label={t("template.role")} hint={t("template.role_hint")} error={touched && !roleOk ? t("template.role_invalid") : undefined}>
            {(id, desc) => <Input id={id} aria-describedby={desc} value={d.role} onChange={(e) => set("role", e.target.value.toLowerCase())} maxLength={32} data-testid="tpl-role" />}
          </Field>
          <div className="tpl-form__pair">
            <Field label={t("template.name_zh")} error={touched && !name ? t("template.name_required") : undefined}>
              {(id) => <Input id={id} lang="zh-CN" value={d.name["zh-CN"] ?? ""} onChange={(e) => setText("name", "zh-CN", e.target.value)} data-testid="tpl-name-zh" />}
            </Field>
            <Field label={t("template.name_en")}>
              {(id) => <Input id={id} lang="en" value={d.name.en ?? ""} onChange={(e) => setText("name", "en", e.target.value)} data-testid="tpl-name-en" />}
            </Field>
          </div>
          <div className="tpl-form__pair">
            <Field label={t("template.desc_zh")}>
              {(id) => <TextArea id={id} lang="zh-CN" rows={2} value={d.description["zh-CN"] ?? ""} onChange={(e) => setText("description", "zh-CN", e.target.value)} />}
            </Field>
            <Field label={t("template.desc_en")}>
              {(id) => <TextArea id={id} lang="en" rows={2} value={d.description.en ?? ""} onChange={(e) => setText("description", "en", e.target.value)} />}
            </Field>
          </div>
          <Field label={t("template.system_prompt")} hint={t("template.system_prompt_hint")}>
            {(id, desc) => (
              <TextArea id={id} aria-describedby={desc} rows={8} className="mono" value={d.systemPrompt} onChange={(e) => set("systemPrompt", e.target.value)} data-testid="tpl-prompt" />
            )}
          </Field>
          <fieldset className="tpl-scopes">
            <legend className="ui-field__label">{t("template.scopes")}</legend>
            <p className="ui-field__hint">{t("template.scopes_hint")}</p>
            <div className="tpl-scopes__grid">
              {McpScope.options.map((s) => (
                <label key={s} className="check">
                  <input
                    type="checkbox"
                    checked={d.mcpScopes.includes(s)}
                    onChange={(e) => set("mcpScopes", e.target.checked ? [...d.mcpScopes, s] : d.mcpScopes.filter((x) => x !== s))}
                    data-testid={`tpl-scope-${s}`}
                  />
                  <span>
                    <code>{s}</code> <span className="muted small">{t(`template.scope.${s}`)}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <TemplateCapabilities tid={tid} value={d.capabilities} onChange={(v) => set("capabilities", v)} readOnly={readOnly} base={capBase} />
          <div className="tpl-form__pair">
            <Field label={t("template.model")} error={touched && !modelOk ? t("template.model_required") : undefined}>
              {(id) => (
                <>
                  <Input id={id} list="tpl-models" value={d.defaultModel} onChange={(e) => set("defaultModel", e.target.value)} data-testid="tpl-model" />
                  <datalist id="tpl-models">
                    {MODEL_SUGGESTIONS.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </>
              )}
            </Field>
            <Field label={t("template.permission_default")}>
              {(id) => (
                <Select id={id} value={d.permissionDefault} onChange={(e) => set("permissionDefault", e.target.value as PermissionPolicy["default"])}>
                  {PermissionMode.options.map((m) => (
                    <option key={m} value={m}>
                      {t(`template.permission.${m}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </fieldset>
        {touched && merged.imageConflict && !readOnly ? (
          <p className="form-error" role="alert" data-testid="tpl-conflict-blocked">
            {t("tplcap.save_blocked")}
          </p>
        ) : null}
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        {!readOnly ? (
          <div className="row tpl-form__foot">
            <Button type="submit" variant="primary" loading={busy} data-testid="tpl-submit">
              {mode === "edit" ? t("common.save") : t("common.create")}
            </Button>
          </div>
        ) : null}
      </form>
    </div>
  );
}
